// @flow

import React from "react";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import { getRouteClientIdParam } from "@selectors";
import * as serviceSelectors from "@modules/service/selectors";
import actionCreators from "@modules/service/actionCreator";
import {
  SystemAccessMainOptionsModal,
  BulkRegistrationEmailsModal,
  SystemAccessRegistrationEmailsModal,
  BulkRegistrationEmailsExportModal,
  SystemAccessConfirmMfaModal,
} from "@modules/service/components";
import { getSchema } from "@utils/schema";
import { split } from "@transformers/schema";
import type {
  DataSecurityGeneralSettings,
  IpAddress,
  RegistrationEmails,
} from "@modules/service/reducer/system-access/types";
import { transformDaysInSeconds } from "@transformers/date";

const schema = getSchema("DataSecurityGeneralSettingsServiceSectionDTO");
const passwordResetSchema = split(schema, [
  "enableResetPasswordRequirement",
  "passwordResetFrequencyManager",
  "passwordResetFrequencyEmployee",
]);

const logOutLockSchema = split(schema, ["inactiveTimeout", "failedLoginLockout"]);

const addIpRestrictionSchema = schema.properties.ipRestrictionWhitelist.items;

const registrationEmailsSchema = getSchema("DataSecurityMyfirstcareServiceSectionDTO");

const standardRegistrationEmailsSchema = split(registrationEmailsSchema, [
  "introductionTextDefault",
  "introductionTextFullHr",
  "introductionTextLineManager",
  "introductionTextAdvancedUser",
  "introductionTextEmployee",
]);

const clientLogoSchema = getSchema("ClientLogoDTO");

type PropsFromState = $Exact<{
  clientId: number,
  showPasswordResetModal: boolean,
  showLogOutLockModal: boolean,
  showAddIpRestrictionModal: boolean,
  dataSecurityGeneralSettings: DataSecurityGeneralSettings,
  showClientLogoModal: boolean,
  showBulkRegistrationEmailsModal: boolean,
  bulkRegistrationEmailTargets: number,
  showStandardRegistrationEmailsModal: boolean,
  registrationEmails: ?RegistrationEmails,
  showBulkRegistrationEmailsExportModal: boolean,
  showEmployeeRegistrationEmailsModal: boolean,
  employeeRegistrationEmailTargets: number,
  confirmEnableMfaModalVisibility: boolean,
}>;

type OwnProps = Object;

type PropsFromDispatch = $Exact<{
  updateDataSecurityGeneralSettings: typeof actionCreators.UpdateDataSecurityGeneralSettings,
  dismissPasswordResetModal: Function,
  dismissLogOutLockModal: Function,
  dismissAddIpRestrictionModal: Function,
  dismissClientLogoModal: Function,
  uploadLogo: typeof actionCreators.UploadClientLogo,
  dismissRegistrationEmailsModal: Function,
  dismissStandardRegistrationEmailsModal: Function,
  sendBulkRegistrationEmails: typeof actionCreators.SendBulkRegistrationEmails,
  updateRegistrationEmails: typeof actionCreators.UpdateRegistrationEmails,
  closeRegistrationEmailsExportModal: Function,
  exportBulkRegistrationEmails: typeof actionCreators.ExportBulkRegistrationEmails,
  dismissEmployeeRegistrationEmailsModal: Function,
  sendEmployeeRegistrationEmails: typeof actionCreators.SendEmployeeRegistrationEmails,
  toggleConfirmEnableMfaModal: typeof actionCreators.ToggleConfirmEnableMfaModal,
}>;

export type PropsFromSystemAccessModals = $Exact<{
  updateSettings: (formData: DataSecurityGeneralSettings) => void,
}>;

type Props = $Exact<{
  clientId: number,
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
  ...PropsFromSystemAccessModals,
}>;

const withSystemAccessModals = (props?: Object) => {
  return (WrappedComponent: *) => {
    class WithSystemAccessModals extends React.Component<Props> {
      handleModalClose = (action: Function) => () => {
        action(false);
      };

      handleModalAction = (payload: Object, action: Function) => (modalPayload?: Object) => {
        action({ ...payload, ...modalPayload });
      };
      // This is needed because some fields must be returned in seconds
      transformFormData = (formData: DataSecurityGeneralSettings) => {
        return {
          ...formData,
          passwordResetFrequencyManager: transformDaysInSeconds(
            formData.passwordResetFrequencyManager,
          ),
          passwordResetFrequencyEmployee: transformDaysInSeconds(
            formData.passwordResetFrequencyEmployee,
          ),
        };
      };

      updateSettings = (formData: DataSecurityGeneralSettings): void => {
        this.props.updateDataSecurityGeneralSettings({
          dataSecurityGeneralSettings: this.transformFormData(formData),
          clientId: this.props.clientId,
        });
      };

      addIpRestriction = (formData: IpAddress) => {
        const ipRestrictionWhitelist = [
          ...this.props.dataSecurityGeneralSettings.ipRestrictionWhitelist,
        ];
        if (Array.isArray(ipRestrictionWhitelist)) {
          const date = new Date();
          formData.activeFrom = date.toISOString();
          formData.isEnabled = true;
          ipRestrictionWhitelist.push(formData);
        }

        this.props.updateDataSecurityGeneralSettings({
          dataSecurityGeneralSettings: {
            ...this.transformFormData(this.props.dataSecurityGeneralSettings),
            ipRestrictionWhitelist,
          },
          clientId: this.props.clientId,
        });
      };

      uploadLogo = (formData: Object) => {
        this.props.uploadLogo({
          clientId: this.props.clientId,
          formData,
        });
        this.props.dismissClientLogoModal();
      };

      sendBulkRegistrationEmailsHandler = () => {
        this.props.sendBulkRegistrationEmails({
          clientId: this.props.clientId,
        });
      };

      updateRegistrationEmails = (formData: Object) => {
        this.props.updateRegistrationEmails({
          clientId: this.props.clientId,
          formData,
          // successMessage: this.props.t("UPLOAD_LOGO_SUCCESS"),
        });
      };

      exportBulkRegistrationEmails = () => {
        this.props.exportBulkRegistrationEmails({
          clientId: this.props.clientId,
        });

        this.props.closeRegistrationEmailsExportModal();
      };

      sendEmployeeRegistrationEmailsHandler = () => {
        this.props.sendEmployeeRegistrationEmails({
          clientId: this.props.clientId,
        });
      };

      render() {
        return (
          <React.Fragment>
            <WrappedComponent {...props} {...this.props} updateSettings={this.updateSettings} />
            {this.props.showPasswordResetModal && this.props.dataSecurityGeneralSettings ? (
              <SystemAccessMainOptionsModal
                onDismissHandler={this.props.dismissPasswordResetModal}
                onSubmit={this.updateSettings}
                schema={passwordResetSchema}
                formData={this.props.dataSecurityGeneralSettings}
                title={this.props.t("PASSWORD_RESET")}
              />
            ) : null}
            {this.props.showLogOutLockModal && this.props.dataSecurityGeneralSettings ? (
              <SystemAccessMainOptionsModal
                onDismissHandler={this.props.dismissLogOutLockModal}
                onSubmit={this.updateSettings}
                schema={logOutLockSchema}
                formData={this.props.dataSecurityGeneralSettings}
                title={this.props.t("LOG_OUT_AND_LOCK")}
              />
            ) : null}
            {this.props.showAddIpRestrictionModal ? (
              <SystemAccessMainOptionsModal
                onDismissHandler={this.props.dismissAddIpRestrictionModal}
                onSubmit={this.addIpRestriction}
                schema={addIpRestrictionSchema}
                title={this.props.t("ADD_IP_RESTRICTION")}
                submitButtonText={this.props.t("ADD")}
                submitButtonIcon="add_box"
              />
            ) : null}
            {this.props.showClientLogoModal ? (
              <SystemAccessMainOptionsModal
                onDismissHandler={this.props.dismissClientLogoModal}
                onSubmit={this.uploadLogo}
                schema={clientLogoSchema}
                title={this.props.t("UPLOAD_LOGO")}
                submitButtonText={this.props.t("UPLOAD")}
              />
            ) : null}

            {this.props.showBulkRegistrationEmailsModal && (
              <BulkRegistrationEmailsModal
                title={this.props.t("SEND_BULK_EMAILS")}
                text={this.props.t("SEND_BULK_EMAILS_TEXT1", {
                  count: this.props.bulkRegistrationEmailTargets,
                })}
                onCancel={this.props.dismissRegistrationEmailsModal}
                onSubmit={this.sendBulkRegistrationEmailsHandler}
              />
            )}

            {this.props.showBulkRegistrationEmailsExportModal && (
              <BulkRegistrationEmailsExportModal
                onCancel={this.props.closeRegistrationEmailsExportModal}
                onSubmit={this.exportBulkRegistrationEmails}
                bulkRegistrationEmailTargets={this.props.bulkRegistrationEmailTargets}
                t={this.props.t}
              />
            )}
            {this.props.showStandardRegistrationEmailsModal && (
              <SystemAccessRegistrationEmailsModal
                onDismissHandler={this.props.dismissStandardRegistrationEmailsModal}
                onSubmit={this.updateRegistrationEmails}
                schema={standardRegistrationEmailsSchema}
                formData={this.props.registrationEmails}
                title={this.props.t("EDIT_STANDARD_REGISTRATION_EMAILS")}
              />
            )}

            {this.props.showEmployeeRegistrationEmailsModal && (
              <BulkRegistrationEmailsModal
                title={this.props.t("SEND_EMPLOYEE_EMAILS")}
                text={this.props.t("SEND_EMPLOYEE_EMAILS_TEXT1", {
                  count: this.props.employeeRegistrationEmailTargets,
                })}
                onCancel={this.props.dismissEmployeeRegistrationEmailsModal}
                onSubmit={this.sendEmployeeRegistrationEmailsHandler}
              />
            )}
            {this.props.confirmEnableMfaModalVisibility ? (
              <SystemAccessConfirmMfaModal
                onCancel={() => {
                  this.props.toggleConfirmEnableMfaModal(false);
                }}
                t={this.props.t}
                onSubmit={() => {
                  this.updateSettings({
                    ...this.props.dataSecurityGeneralSettings,
                    multiFactorAuthenticationEnabled: true,
                  });
                  this.props.toggleConfirmEnableMfaModal(false);
                }}
              />
            ) : null}
          </React.Fragment>
        );
      }
    }

    const mapStateToProps = (state, ownProps): PropsFromState => {
      const clientId = getRouteClientIdParam(state) || ownProps.clientId || ownProps.client.id;

      return {
        clientId,
        showPasswordResetModal: serviceSelectors.getShowSystemAccessPasswordResetModal(state),
        showLogOutLockModal: serviceSelectors.getShowSystemAccessLogOutLockModal(state),
        showAddIpRestrictionModal: serviceSelectors.getShowAddIpRestrictionModal(state),
        dataSecurityGeneralSettings: serviceSelectors.dataSecurityGeneralSettings(state),
        showClientLogoModal: serviceSelectors.getShowClientLogoModal(state),
        showBulkRegistrationEmailsModal: serviceSelectors.getShowBulkRegistrationEmailsModal(state),
        showEmployeeRegistrationEmailsModal: serviceSelectors.getShowEmployeeRegistrationEmailsModal(
          state,
        ),
        showBulkRegistrationEmailsExportModal: serviceSelectors.getShowBulkRegistrationEmailsExportModal(
          state,
        ),
        bulkRegistrationEmailTargets: serviceSelectors.getBulkRegistrationEmailTargets(state),
        employeeRegistrationEmailTargets: serviceSelectors.getEmployeeRegistrationEmailTargets(
          state,
        ),
        showStandardRegistrationEmailsModal: serviceSelectors.getShowStandardRegistrationEmailsModal(
          state,
        ),
        registrationEmails: serviceSelectors.getRegistrationEmails(state),
        confirmEnableMfaModalVisibility: serviceSelectors.showConfirmEnableMfaModal(state),
      };
    };

    const mapDispatchToProps: PropsFromDispatch = {
      updateDataSecurityGeneralSettings: actionCreators.UpdateDataSecurityGeneralSettings,
      dismissPasswordResetModal: () => actionCreators.ToggleSystemAccessPasswordResetModal(false),
      dismissLogOutLockModal: () => actionCreators.ToggleSystemAccessLogOutLockModal(false),
      dismissAddIpRestrictionModal: () => actionCreators.ToggleAddIpRestrictionModal(false),
      dismissClientLogoModal: () => actionCreators.ToggleClientLogoModal(false),
      dismissRegistrationEmailsModal: () => actionCreators.ToggleRegistrationEmailsModal(false),
      dismissStandardRegistrationEmailsModal: () =>
        actionCreators.ToggleStandardRegistrationEmailsModal(false),
      uploadLogo: actionCreators.UploadClientLogo,
      sendBulkRegistrationEmails: actionCreators.SendBulkRegistrationEmails,
      updateRegistrationEmails: actionCreators.UpdateRegistrationEmails,
      closeRegistrationEmailsExportModal: () =>
        actionCreators.ToggleRegistrationEmailsExportModal(false),
      exportBulkRegistrationEmails: actionCreators.ExportBulkRegistrationEmails,
      dismissEmployeeRegistrationEmailsModal: () =>
        actionCreators.ToggleEmployeeRegistrationEmailsModal(false),
      sendEmployeeRegistrationEmails: actionCreators.SendEmployeeRegistrationEmails,
      toggleConfirmEnableMfaModal: actionCreators.ToggleConfirmEnableMfaModal,
    };

    return connect(mapStateToProps, mapDispatchToProps)(localize()(WithSystemAccessModals));
  };
};

export default withSystemAccessModals;
